import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { ExclamationTriangleIcon, UserIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";

import { switchToUser } from "../../user/utils/user";

import { db } from "../../data/utils/data";
import { get, post } from "../utils/api";

function ImpersonationModal({ isOpen, onClose, t }) {
  const [impersonateUserId, setImpersonateUserId] = useState("");
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [impersonateError, setImpersonateError] = useState("");
  const [impersonatableUsers, setImpersonatableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");

  const [selectedUserDisplay, setSelectedUserDisplay] = useState("");
  const [hasLocalData, setHasLocalData] = useState(false);
  const [checkingData, setCheckingData] = useState(true);

  const filteredUsers = userSearchQuery
    ? impersonatableUsers.filter(
        (u) =>
          u.email.toLowerCase().indexOf(userSearchQuery.toLowerCase()) !== -1,
      )
    : impersonatableUsers;

  const handleImpersonateUserChange = (newUserId) => {
    setImpersonateUserId(newUserId);
    if (newUserId) {
      const selected = impersonatableUsers.find((u) => u.id === newUserId);
      setSelectedUserDisplay(selected ? selected.email : "");
    } else {
      setSelectedUserDisplay("");
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await get("/users/impersonate/list/");
      setImpersonatableUsers(response.data);
    } catch (error) {
      console.error("Failed to load users:", error);
      setImpersonateError(
        t("user.failedToLoadUsers", "Failed to load users. Please try again."),
      );
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleImpersonate = async (e) => {
    e.preventDefault();
    if (!impersonateUserId || hasLocalData) return;

    setImpersonateLoading(true);
    setImpersonateError("");

    try {
      const response = await post("/users/impersonate/", {
        user_id: parseInt(impersonateUserId),
      });

      const userResponse = await get("/users/current/", response.data.token);
      const userInfo = userResponse.data;

      switchToUser(response.data.token, userInfo);
    } catch (error) {
      setImpersonateError(
        error.response?.data?.error ||
          t("user.failedToImpersonate", "Failed to impersonate user"),
      );
      setImpersonateLoading(false);
    }
  };

  const handleClose = () => {
    setImpersonateUserId("");
    setImpersonateError("");
    setUserSearchQuery("");
    setSelectedUserDisplay("");
    onClose();
  };

  const checkLocalData = async () => {
    setCheckingData(true);
    try {
      const dataCount = await db.data.count();
      const imageCount = await db.images.count();
      setHasLocalData(dataCount > 0 || imageCount > 0);
    } catch (error) {
      console.error("Error checking local data:", error);
    } finally {
      setCheckingData(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      checkLocalData();
      if (impersonatableUsers.length === 0) {
        fetchUsers();
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6">
          <div>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100">
              <UserIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t("user.impersonateUser", "Impersonate User")}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {t(
                    "user.impersonateUserSelectDescription",
                    "Select a user to view the app as that user.",
                  )}
                </p>
              </div>
            </div>
          </div>

          {hasLocalData && !checkingData ? (
            <div className="mt-5 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon
                    className="h-5 w-5 text-red-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {t("user.localDataDetected", "Local data detected")}
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>
                      {t(
                        "user.impersonateWarningDataMessage",
                        "You have unsaved data in your local tables. Impersonating now would risk losing this data as the local database is cleared upon exiting impersonation mode. Please submit or clear your local data first.",
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleImpersonate} className="mt-5">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t("user.selectUser", "Select User")}
                </label>
                {loadingUsers ? (
                  <div className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50 text-gray-500">
                    {t("user.loadingUsers", "Loading users...")}
                  </div>
                ) : (
                  <Combobox
                    value={impersonateUserId}
                    onChange={handleImpersonateUserChange}
                  >
                    <div className="relative mt-1">
                      <ComboboxInput
                        className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 sm:text-sm"
                        displayValue={() => selectedUserDisplay}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        placeholder={t(
                          "user.searchUsersByEmail",
                          "Search users by email...",
                        )}
                      />
                      <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredUsers.length === 0 &&
                        userSearchQuery !== "" ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            {t("user.noUsersFound", "No users found.")}
                          </div>
                        ) : (
                          filteredUsers.slice(0, 50).map((user) => (
                            <ComboboxOption
                              key={user.id}
                              value={user.id}
                              className={({ focus }) =>
                                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                  focus
                                    ? "bg-orange-100 text-orange-900"
                                    : "text-gray-900"
                                }`
                              }
                            >
                              {({ selected, focus }) => (
                                <>
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <span
                                        className={`block truncate ${
                                          selected
                                            ? "font-medium"
                                            : "font-normal"
                                        }`}
                                      >
                                        {user.email}
                                      </span>
                                    </div>
                                    <div className="flex gap-1">
                                      {user.is_superuser && (
                                        <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                                          {t("user.admin", "Admin")}
                                        </span>
                                      )}
                                      {user.is_reviewer &&
                                        !user.is_superuser && (
                                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                                            {t("user.reviewer", "Reviewer")}
                                          </span>
                                        )}
                                    </div>
                                  </div>
                                  {selected ? (
                                    <span
                                      className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        focus
                                          ? "text-orange-600"
                                          : "text-orange-600"
                                      }`}
                                    >
                                      ✓
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </ComboboxOption>
                          ))
                        )}
                      </ComboboxOptions>
                    </div>
                  </Combobox>
                )}
                {filteredUsers.length > 50 && (
                  <p className="mt-1 text-xs text-gray-500">
                    {t("user.showingUsersOf", {
                      count: 50,
                      total: filteredUsers.length,
                      defaultValue: `Showing 50 of ${filteredUsers.length} users. Continue typing to narrow results.`,
                    })}
                  </p>
                )}
                {impersonateError && (
                  <p className="mt-2 text-sm text-red-600 border border-red-200 bg-red-50 p-2 rounded">
                    {impersonateError}
                  </p>
                )}
              </div>
              <div className="mt-5 sm:mt-6">
                <button
                  type="submit"
                  disabled={
                    impersonateLoading ||
                    loadingUsers ||
                    !impersonateUserId ||
                    hasLocalData
                  }
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-orange-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {impersonateLoading
                    ? t("user.impersonating", "Impersonating...")
                    : t("user.impersonateUser", "Impersonate User")}
                </button>
              </div>
            </form>
          )}
          <div className="mt-3">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none sm:text-sm"
            >
              {hasLocalData
                ? t("common.close", "Close")
                : t("common.cancel", "Cancel")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withTranslation()(ImpersonationModal);
